/**
 * Профиль пользователя (ТЗ, раздел 7).
 *
 * Живёт на сервере и синхронизируется между устройствами — это прямое
 * требование 7.4. До этого этапа настройки лежали в localStorage, и
 * рабочее место не переезжало вместе с человеком.
 *
 * Разрешение порогов вынесено сюда, а не в Core и не в терминал, по той
 * же причине, что и арифметика баров: решение «показывать ли сигнал»
 * обязаны принимать одинаково сервер (для уведомлений) и клиент (для
 * графика). Две реализации разошлись бы, и человек видел бы на экране
 * одно, а в уведомлении другое.
 */
import { DEFAULT_THRESHOLDS } from './threshold.js';
export const SETTINGS_VERSION = 1;
export function instrumentKey(ref) {
    return `${ref.venue}|${ref.symbol}|${ref.timeframe}`;
}
export const NO_STUDIO_PROGRESS = { explored: [], badges: [] };
/** Сколько версий держать на одну систему и всего: профиль не резиновый. */
const VERSIONS_PER_OBJECT = 15;
const VERSIONS_TOTAL = 300;
export function versionsFrom(raw) {
    if (!Array.isArray(raw))
        return [];
    const out = [];
    for (const entry of raw) {
        if (typeof entry !== 'object' || entry === null)
            continue;
        const value = entry;
        const kind = value['kind'];
        if (typeof value['id'] !== 'string' || typeof value['objectId'] !== 'string' || (kind !== 'bot' && kind !== 'indicator'))
            continue;
        if (typeof value['version'] !== 'number' || typeof value['at'] !== 'number' || typeof value['snapshot'] !== 'object' || value['snapshot'] === null)
            continue;
        const result = value['result'];
        const okResult = result && ['profit', 'trades', 'drawdown', 'bars', 'from', 'to'].every((key) => typeof result[key] === 'number' && Number.isFinite(result[key]));
        out.push({
            id: value['id'],
            objectId: value['objectId'],
            kind,
            version: value['version'],
            at: value['at'],
            snapshot: value['snapshot'],
            ...(typeof value['note'] === 'string' ? { note: value['note'].slice(0, 80) } : {}),
            ...(okResult ? { result: { ...result, winRate: typeof result['winRate'] === 'number' ? result['winRate'] : null } } : {}),
            ...(scoreOf(value['score']) ? { score: scoreOf(value['score']) } : {}),
        });
    }
    return trimVersions(out);
}
function scoreOf(raw) {
    if (typeof raw !== 'object' || raw === null)
        return null;
    const value = raw;
    if (typeof value['signals'] !== 'number' || typeof value['grade'] !== 'string')
        return null;
    const rate = value['rate'];
    return { signals: value['signals'], rate: typeof rate === 'number' && Number.isFinite(rate) ? rate : null, grade: value['grade'].slice(0, 20) };
}
/** Последние версии каждой системы и не больше общего предела. */
export function trimVersions(list) {
    const byObject = new Map();
    for (const entry of list)
        byObject.set(entry.objectId, [...(byObject.get(entry.objectId) ?? []), entry]);
    const kept = [...byObject.values()].flatMap((entries) => entries.sort((a, b) => a.at - b.at).slice(-VERSIONS_PER_OBJECT));
    return kept.sort((a, b) => a.at - b.at).slice(-VERSIONS_TOTAL);
}
export const NO_JOURNEY = { path: null, welcomed: false, missions: [], moments: [], active: null };
const JOURNEY_PATHS = ['trader', 'explorer', 'creator', 'builder'];
export function journeyFrom(value) {
    if (typeof value !== 'object' || value === null)
        return NO_JOURNEY;
    const raw = value;
    const marks = (entry, limit) => {
        if (!Array.isArray(entry))
            return [];
        const seen = new Set();
        const out = [];
        for (const item of entry) {
            if (typeof item !== 'object' || item === null)
                continue;
            const { id, at } = item;
            if (typeof id !== 'string' || id === '' || id.length > 64 || seen.has(id))
                continue;
            if (typeof at !== 'number' || !Number.isFinite(at))
                continue;
            seen.add(id);
            out.push({ id, at });
        }
        return out.slice(-limit);
    };
    return {
        path: JOURNEY_PATHS.includes(raw['path']) ? raw['path'] : null,
        welcomed: raw['welcomed'] === true,
        missions: marks(raw['missions'], 100),
        moments: marks(raw['moments'], 100),
        active: typeof raw['active'] === 'string' && raw['active'] !== '' && raw['active'].length <= 64 ? raw['active'] : null,
    };
}
function normalizeStudio(value) {
    if (typeof value !== 'object' || value === null)
        return null;
    const raw = value;
    const ids = (entry, limit) => Array.isArray(entry)
        ? [...new Set(entry.filter((id) => typeof id === 'string' && id !== '' && id.length <= 64))].slice(-limit)
        : [];
    return { explored: ids(raw.explored, 300), badges: ids(raw.badges, 60) };
}
export const NO_MARKS = { journalClearedAt: 0, journalSeenAt: 0, notificationsSeenAt: 0 };
function normalizeMarks(value) {
    if (typeof value !== 'object' || value === null)
        return NO_MARKS;
    const raw = value;
    const at = (entry) => (typeof entry === 'number' && Number.isFinite(entry) && entry > 0 ? entry : 0);
    return {
        journalClearedAt: at(raw.journalClearedAt),
        journalSeenAt: at(raw.journalSeenAt),
        notificationsSeenAt: at(raw.notificationsSeenAt),
    };
}
export const DEFAULT_SETTINGS = {
    version: SETTINGS_VERSION,
    favourites: [],
    bots: { enabled: 'all', params: {}, instruments: {} },
    thresholds: { global: DEFAULT_THRESHOLDS, byBot: {}, byBotSymbol: {} },
    belowMode: 'dim',
    /*
     * Новый человек видит тёмный терминал.
     *
     * Платформа нарисована под тёмный: свечение по краям панелей и
     * цветные зоны на графике задуманы на нём. «Как в системе» остаётся
     * выбором, но не умолчанием — первое впечатление не должно зависеть
     * от того, какую тему случайно выставил телефон.
     */
    theme: 'dark',
    language: 'system',
    notifications: {
        channels: { inApp: true, browser: false, telegram: false },
        limits: {
            // Выше порога отображения по умолчанию: видеть — не то же, что
            // быть разбуженным.
            /*
             * Оба ограничения по умолчанию выключены — и у ботов, и у
             * индикаторов.
             *
             * Новичок, не знающий о фильтре, иначе не получал бы часть
             * уведомлений и не мог бы понять почему: в интерфейсе, где он
             * их ждёт, причины не видно. Включённое самим человеком работает
             * как раньше.
             */
            bot: { scoreEnabled: false, minScore: 80, rateEnabled: false, maxPerHour: 10 },
            /*
             * У индикаторов порог по умолчанию выключен.
             *
             * Он и раньше к ним не применялся, и включить его по умолчанию
             * значило бы молча отобрать у людей часть уведомлений при
             * обновлении. Человек, настроивший порог внутри индикатора,
             * вправе не знать о существовании второго — пока сам его не
             * включит.
             */
            rule: { scoreEnabled: false, minScore: 80, rateEnabled: false, maxPerHour: 10 },
        },
        symbols: [],
        bots: [],
        quietHours: { enabled: false, from: 22 * 60, to: 8 * 60 },
    },
    workspace: null,
    updatedAt: 0,
};
/** Ключ избранного: инструмент принадлежит источнику. */
export function favouriteKey(venue, symbol) {
    return `${venue}|${symbol}`;
}
export function botSymbolKey(botId, symbol) {
    return `${botId}|${symbol}`;
}
/**
 * Разрешение порогов по цепочке наследования.
 *
 * Самый частный уровень выигрывает целиком, а не по одному полю:
 * половинчатое слияние («long отсюда, short оттуда») невозможно объяснить
 * человеку, глядя на интерфейс.
 */
export function resolveThresholds(settings, botId, symbol) {
    return (settings.byBotSymbol[botSymbolKey(botId, symbol)] ??
        settings.byBot[botId] ??
        settings.global);
}
export function isBotEnabled(settings, botId) {
    return settings.enabled === 'all' || settings.enabled.includes(botId);
}
/** Проходит ли сигнал пороги профиля. */
export function signalPasses(signal, settings) {
    if (!isBotEnabled(settings.bots, signal.botId))
        return false;
    const thresholds = resolveThresholds(settings.thresholds, signal.botId, signal.symbol);
    return signal.score >= thresholdOf(thresholds, signal.side);
}
/** Событие без направления порогом Score не отсекается: см. thresholdFor. */
function thresholdOf(thresholds, side) {
    if (side === 'ALERT')
        return 0;
    return side === 'LONG' ? thresholds.long : thresholds.short;
}
/** Внутри тихих часов? `minutesUtc` — минуты от полуночи UTC. */
export function inQuietHours(quiet, minutesUtc) {
    if (!quiet.enabled)
        return false;
    if (quiet.from === quiet.to)
        return false;
    // Интервал через полночь: 22:00 → 08:00.
    if (quiet.from > quiet.to)
        return minutesUtc >= quiet.from || minutesUtc < quiet.to;
    return minutesUtc >= quiet.from && minutesUtc < quiet.to;
}
/** Должен ли сигнал породить уведомление. Пороги отображения здесь ни при чём. */
export function shouldNotify(signal, settings, now) {
    const notifications = settings.notifications;
    if (signal.status !== 'ACTIVE')
        return false;
    /*
     * Пересчёт по истории не будит человека.
     *
     * Отметки на графике показывают, где правило сработало бы раньше;
     * человек, создавший правило, получил бы тридцать уведомлений о
     * событиях прошлого месяца — и справедливо счёл бы это поломкой.
     * Уведомление порождает только то, что произошло на самом деле.
     */
    if (signal.origin === 'history')
        return false;
    /*
     * Правило человек включает сам, флажком «Уведомлять меня».
     *
     * Списки ботов к нему не применяются: правило — не бот, его нет ни в
     * настройках ботов, ни в списке разрешённых. Проверять его этими
     * фильтрами значило бы молча выключить уведомления, которые человек
     * только что включил.
     */
    if (signal.sourceKind === 'rule') {
        /*
         * Порог у пользовательского сигнала один — его собственный.
         *
         * Общий `minScore` сюда намеренно не применяется. Человек уже
         * выставил порог в конструкторе и видел прямо там, сколько
         * срабатываний он оставляет; второй фильтр поверх означал бы, что
         * настроенное число ничего не решает, а сигналы пропадают по
         * причине, которой на экране не было.
         *
         * Для авторских ботов всё наоборот: их Score считает чужой код, и
         * общий порог — единственная защита от бота, раздающего слабые
         * сигналы пачками.
         */
        if (signal.meta?.rule?.notify !== true)
            return false;
        if (notifications.symbols.length > 0 && !notifications.symbols.includes(signal.symbol)) {
            return false;
        }
        const at = new Date(now);
        return !inQuietHours(notifications.quietHours, at.getUTCHours() * 60 + at.getUTCMinutes());
    }
    if (!isBotEnabled(settings.bots, signal.botId))
        return false;
    /*
     * Порога силы в уведомлениях больше нет — ни у ботов, ни у индикаторов.
     *
     * Сила задаётся в самом источнике: порог индикатора — в его
     * настройках, бот ставит оценку сам. Второй порог поверх жил на
     * странице настроек, где его никто не связывал с молчащим
     * уведомлением, и оттуда убран. Поля `scoreEnabled`/`minScore` в
     * профиле остаются, чтобы не ломать его формат, но не читаются: у
     * включивших их когда-то иначе остался бы фильтр, которого не видно.
     * От потока слабых сигналов защищает предел частоты.
     */
    if (notifications.symbols.length > 0 && !notifications.symbols.includes(signal.symbol)) {
        return false;
    }
    if (notifications.bots.length > 0 && !notifications.bots.includes(signal.botId)) {
        return false;
    }
    const date = new Date(now);
    const minutes = date.getUTCHours() * 60 + date.getUTCMinutes();
    if (inQuietHours(notifications.quietHours, minutes))
        return false;
    return true;
}
/**
 * Приведение сохранённого профиля к текущей форме.
 *
 * Настройки живут дольше кода: профиль мог быть записан прежней версией
 * терминала. Молча подставить undefined в порог значит спрятать все
 * сигналы без объяснения причины.
 */
export function normalizeSettings(raw) {
    if (typeof raw !== 'object' || raw === null)
        return DEFAULT_SETTINGS;
    const input = raw;
    const clampScore = (value, fallback) => {
        return scoreOr(value, fallback);
    };
    const thresholds = (value, fallback) => {
        if (typeof value !== 'object' || value === null)
            return fallback;
        const t = value;
        return {
            long: clampScore(t.long, fallback.long),
            short: clampScore(t.short, fallback.short),
        };
    };
    const thresholdMap = (value) => {
        if (typeof value !== 'object' || value === null)
            return {};
        const out = {};
        for (const [key, entry] of Object.entries(value)) {
            out[key] = thresholds(entry, DEFAULT_THRESHOLDS);
        }
        return out;
    };
    const incomingBots = input.bots ?? DEFAULT_SETTINGS.bots;
    const incomingNotifications = input.notifications ?? DEFAULT_SETTINGS.notifications;
    const defaults = DEFAULT_SETTINGS.notifications;
    return {
        version: SETTINGS_VERSION,
        favourites: Array.isArray(input.favourites)
            ? input.favourites.filter((entry) => typeof entry === 'string')
            : [],
        bots: {
            enabled: incomingBots.enabled === 'all' || Array.isArray(incomingBots.enabled)
                ? incomingBots.enabled
                : 'all',
            params: typeof incomingBots.params === 'object' && incomingBots.params !== null
                ? incomingBots.params
                : {},
            instruments: normalizeInstruments(incomingBots.instruments),
        },
        thresholds: {
            global: thresholds(input.thresholds?.global, DEFAULT_THRESHOLDS),
            byBot: thresholdMap(input.thresholds?.byBot),
            byBotSymbol: thresholdMap(input.thresholds?.byBotSymbol),
        },
        belowMode: input.belowMode === 'hide' ? 'hide' : 'dim',
        // Незнакомое значение — это «не выбрано»: молча оставить в профиле
        // мусор значит однажды показать интерфейс без темы вовсе.
        /*
         * Выбор «как в системе» сохраняется; пустое и незнакомое — это
         * «ещё не выбирали», и тогда действует умолчание. Раньше они
         * сливались в одно, и это было безразлично, пока умолчанием и было
         * «как в системе». Теперь умолчание другое, и человек, сам
         * выбравший системную тему, не должен её потерять.
         */
        theme: input.theme === 'dark' || input.theme === 'light' || input.theme === 'system'
            ? input.theme
            : DEFAULT_SETTINGS.theme,
        language: input.language === 'ru' || input.language === 'en' ? input.language : 'system',
        notifications: {
            channels: {
                inApp: incomingNotifications.channels?.inApp !== false,
                browser: incomingNotifications.channels?.browser === true,
                telegram: incomingNotifications.channels?.telegram === true,
            },
            limits: {
                bot: normalizeLimits(incomingNotifications, 'bot', defaults.limits.bot),
                rule: normalizeLimits(incomingNotifications, 'rule', defaults.limits.rule),
            },
            symbols: Array.isArray(incomingNotifications.symbols)
                ? incomingNotifications.symbols
                : [],
            bots: Array.isArray(incomingNotifications.bots) ? incomingNotifications.bots : [],
            quietHours: {
                enabled: incomingNotifications.quietHours?.enabled === true,
                from: clampMinutes(incomingNotifications.quietHours?.from, defaults.quietHours.from),
                to: clampMinutes(incomingNotifications.quietHours?.to, defaults.quietHours.to),
            },
        },
        workspace: normalizeWorkspace(input.workspace),
        ...(input.marks ? { marks: normalizeMarks(input.marks) } : {}),
        updatedAt: typeof input.updatedAt === 'number' ? input.updatedAt : 0,
    };
}
/**
 * Порог из сохранённого профиля.
 *
 * `null`, `undefined` и пустая строка — это «значения нет», а не ноль.
 * `Number(null)` равен нулю, и без этой проверки испорченный порог
 * молча превратился бы в «уведомлять обо всём».
 */
function scoreOr(value, fallback) {
    if (value === null || value === undefined || value === '')
        return fallback;
    const numeric = Number(value);
    if (!Number.isFinite(numeric))
        return fallback;
    return Math.min(Math.max(Math.round(numeric), 0), 100);
}
/**
 * Ограничения одного рода источников из сохранённого профиля.
 *
 * Здесь же переезд со старой формы, где порог и частота были общими:
 * `minScore` и `maxPerHourPerBot` лежали прямо в уведомлениях. Профили
 * с ними уже сохранены у людей, и потерять настроенное при обновлении
 * недопустимо — человек не поймёт, почему его пороги вдруг сбросились.
 *
 * Старый общий порог достаётся ботам: к индикаторам он и не
 * применялся, и приписать его им сейчас значило бы включить фильтр,
 * которого человек не включал. Старая частота достаётся обоим — она
 * работала для всех.
 */
function normalizeLimits(incoming, kind, fallback) {
    const own = incoming.limits?.[kind];
    const legacyScore = kind === 'bot' ? incoming.minScore : undefined;
    const legacyRate = incoming.maxPerHourPerBot;
    const rate = Number(own?.maxPerHour ?? legacyRate ?? fallback.maxPerHour);
    return {
        /*
         * Профиль старой версии с общим порогом или частотой — это
         * включённое ограничение: так оно тогда и работало. Новое умолчание
         * («выключено») к таким не применяется, иначе обновление молча
         * сняло бы человеку фильтр, который он настраивал.
         */
        scoreEnabled: own?.scoreEnabled ?? (legacyScore !== undefined ? true : fallback.scoreEnabled),
        minScore: scoreOr(own?.minScore ?? legacyScore, fallback.minScore),
        rateEnabled: own?.rateEnabled ?? (legacyRate !== undefined ? true : fallback.rateEnabled),
        /*
         * Ноль сохраняется, мусор — нет.
         *
         * Ноль при включённом ограничении означает «молчать», и это
         * осознанное желание. А `NaN` из испорченного профиля означает, что
         * значения нет вовсе, и подменять его нулём значило бы выключить
         * человеку уведомления из-за чужой ошибки.
         */
        maxPerHour: Number.isFinite(rate) ? Math.max(0, Math.round(rate)) : fallback.maxPerHour,
    };
}
/**
 * Приведение выбора потоков.
 *
 * Записи с неполными полями отбрасываются поштучно: одна битая строка
 * не должна обнулить весь выбор и молча вернуть бота на все потоки.
 */
function normalizeInstruments(value) {
    if (typeof value !== 'object' || value === null)
        return {};
    const out = {};
    for (const [botId, list] of Object.entries(value)) {
        if (!Array.isArray(list))
            continue;
        const refs = [];
        const seen = new Set();
        for (const entry of list) {
            if (typeof entry !== 'object' || entry === null)
                continue;
            const ref = entry;
            if (typeof ref.venue !== 'string' ||
                typeof ref.symbol !== 'string' ||
                typeof ref.timeframe !== 'string') {
                continue;
            }
            const key = `${ref.venue}|${ref.symbol}|${ref.timeframe}`;
            if (seen.has(key))
                continue;
            seen.add(key);
            refs.push({
                venue: ref.venue,
                symbol: ref.symbol,
                timeframe: ref.timeframe,
            });
        }
        if (refs.length > 0)
            out[botId] = refs;
    }
    return out;
}
function clampMinutes(value, fallback) {
    // Та же ловушка, что и с порогами: Number(null) === 0, и полночь
    // подставилась бы вместо сохранённого времени.
    if (value === null || value === undefined || value === '')
        return fallback;
    const numeric = Number(value);
    if (!Number.isFinite(numeric))
        return fallback;
    return Math.min(Math.max(Math.round(numeric), 0), 24 * 60 - 1);
}
function normalizeWorkspace(value) {
    if (typeof value !== 'object' || value === null)
        return null;
    const workspace = value;
    if (typeof workspace.venue !== 'string' || typeof workspace.symbol !== 'string') {
        return null;
    }
    return {
        venue: workspace.venue,
        symbol: workspace.symbol,
        timeframe: (workspace.timeframe ?? '1H'),
        indicators: Array.isArray(workspace.indicators) ? workspace.indicators : [],
        // Профиль переживает версии терминала: у сохранённого раньше
        // рабочего места этих полей просто нет.
        chartStyle: typeof workspace.chartStyle === 'string' ? workspace.chartStyle : 'candles',
        ...(workspace.scale !== undefined ? { scale: workspace.scale } : {}),
        ...(typeof workspace.look === 'object' && workspace.look !== null ? { look: workspace.look } : {}),
        drawings: Array.isArray(workspace.drawings) ? workspace.drawings : [],
        rules: Array.isArray(workspace.rules) ? workspace.rules : [],
        ...(Array.isArray(workspace.signals) ? { signals: workspace.signals } : {}),
        ...(Array.isArray(workspace.watches) ? { watches: workspace.watches } : {}),
        ...(Array.isArray(workspace.divergenceWatches)
            ? { divergenceWatches: workspace.divergenceWatches }
            : {}),
        ...(Array.isArray(workspace.indicatorWatches) ? { indicatorWatches: workspace.indicatorWatches } : {}),
        ...(Array.isArray(workspace.drawingTemplates) ? { drawingTemplates: workspace.drawingTemplates } : {}),
        ...(Array.isArray(workspace.customColors)
            ? {
                customColors: workspace.customColors
                    .filter((color) => typeof color === 'string' && /^#[0-9a-f]{6}$/i.test(color))
                    .slice(-20),
            }
            : {}),
        ...(Array.isArray(workspace.alerts) ? { alerts: workspace.alerts } : {}),
        ...(Array.isArray(workspace.shownBots)
            ? { shownBots: workspace.shownBots.filter((id) => typeof id === 'string' && id !== '').slice(-50) }
            : {}),
        ...(() => {
            const studio = normalizeStudio(workspace.studio);
            return studio ? { studio } : {};
        })(),
        ...(Array.isArray(workspace.radars) ? { radars: workspace.radars.slice(0, 50) } : {}),
        ...(workspace.journey !== undefined ? { journey: journeyFrom(workspace.journey) } : {}),
        ...(Array.isArray(workspace.versions) ? { versions: versionsFrom(workspace.versions) } : {}),
        bots: Array.isArray(workspace.bots) ? workspace.bots : [],
    };
}
//# sourceMappingURL=settings.js.map